// GiftsView.jsx
// Top-level gifts management view. Admin and Manager roles only.
// Zone Framework compliant:
//   Zone 1 — ZoneOneHeader (navigation, user badge, help)
//   Zone 2 — Filter bar + action bar (data controls)
//   Zone 3 — ZoneThreeFooter (branding)
// Path: src/views/GiftsView.jsx

import React, { useState, useEffect, useCallback } from "react";
import {
  Gift,
  Search,
  Plus,
  Download,
  CheckSquare,
  Square,
  Receipt,
  ChevronUp,
  ChevronDown,
  AlertCircle,
  Loader,
  RefreshCw,
  X,
  Layers,
} from "lucide-react";
import { FONT_SIZES, FONT_FAMILY, CHAPTER_ID, ICON_SIZES } from "../constants/appConfig";
import { formatCurrency, getGiftTypeLabel } from "../constants/giftTypes";
import { giftsService } from "../services/giftsService";
import { getFiscalDates } from "../utils/fiscalYear";
import { GiftReceiptModal } from "../components/features/GiftReceiptModal";
import { GiftEntryForm } from "../components/features/GiftEntryForm";
import { useApp } from "../context/AppContext";
import {
  ZoneOneHeader,
  ZoneThreeFooter,
} from "../components/zones/ZoneHeaderFooter";
import { ZoneTwoToolbar } from "../components/zones/ZoneTwoToolbar";
import { ViewLayout } from "../components/ui/ViewLayout";
import { BatchEntryPanel } from "../components/gifts/BatchEntryPanel";
import { batchService } from "../services/batchService";
import { Modal } from "../components/ui/Modal";
import { ActionButton } from "../components/ui/ActionButton";
import { HELP } from "../constants/helpContent";

// ── Type color dots ───────────────────────────────────────────
var TYPE_COLORS = {
  cash: "#3b82f6",
  inkind: "#8b5cf6",
  stock: "#10b981",
  daf: "#f59e0b",
};

// ── Ack status badge (card-safe) ──────────────────────────────
var AckBadge = function (props) {
  var gift = props.gift;
  var palette = props.palette;

  if (gift.acknowledgedAt) {
    return (
      <span
        style={{
          fontSize: 10,
          fontWeight: 700,
          padding: "2px 8px",
          borderRadius: 999,
          background: "#dcfce7",
          color: "#166534",
          border: "1px solid #bbf7d0",
        }}
      >
        Acknowledged
      </span>
    );
  }

  if (gift.receiptNumber) {
    return (
      <span
        style={{
          fontSize: 10,
          fontWeight: 700,
          padding: "2px 8px",
          borderRadius: 999,
          background: "#fef3c7",
          color: "#92400e",
          border: "1px solid #fde68a",
        }}
      >
        Receipted
      </span>
    );
  }

  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 700,
        padding: "2px 8px",
        borderRadius: 999,
        background: "#f3f4f6",
        color: palette.text3,
        border: "1px solid " + palette.cardBorder,
      }}
    >
      Pending
    </span>
  );
};

// ── CSV export (utility — no DOM changes) ─────────────────────
var exportCsv = function (rows, funds, members) {
  var getFundName = function (fundId) {
    if (!funds || !fundId) return "";
    var f = funds.find(function (x) {
      return x.id === fundId;
    });
    return f ? f.name : "";
  };
  var getMemberName = function (memberId) {
    if (!members || !memberId) return "";
    var m = members.find(function (x) {
      return x.id === memberId;
    });
    return m
      ? ((m.firstName || "") + " " + (m.lastName || "")).trim()
      : memberId;
  };

  var headers = [
    "Receipt #",
    "Gift Date",
    "Donor",
    "Type",
    "Gross Amount",
    "G&S FMV",
    "Net Deductible",
    "Fund",
    "Payment Method",
    "Acknowledged",
    "Notes",
  ];

  var csvRows = rows.map(function (g) {
    return [
      g.receiptNumber || "",
      g.giftDate || "",
      getMemberName(g.memberId),
      getGiftTypeLabel(g.giftType),
      g.amount != null ? g.amount : "",
      g.fairMarketValue || "",
      g.deductibleAmount != null ? g.deductibleAmount : "",
      g.fund?.name || getFundName(g.fundId),
      g.paymentMethod || "",
      g.acknowledgedAt ? "Yes" : "No",
      g.notes || "",
    ]
      .map(function (v) {
        var s = String(v);
        if (s.includes(",") || s.includes('"') || s.includes("\n")) {
          return '"' + s.replace(/"/g, '""') + '"';
        }
        return s;
      })
      .join(",");
  });

  var csv = [headers.join(",")].concat(csvRows).join("\n");
  var blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  var url = URL.createObjectURL(blob);
  var a = document.createElement("a");
  a.href = url;
  a.download = "gifts-export-" + new Date().toISOString().slice(0, 10) + ".csv";
  a.click();
  URL.revokeObjectURL(url);
};

// ── Main Component ────────────────────────────────────────────
export var GiftsView = function () {
  var ctx = useApp();
  var palette = ctx.palette;
  var funds = ctx.funds;
  var activeFunds = ctx.activeFunds;
  var members = ctx.members;
  var currentUser = ctx.currentUser;
  var navigate = ctx.navigate;
  var showMessage = ctx.showMessage;
  var showLoading = ctx.showLoading;
  var hideLoading = ctx.hideLoading;
  var isAdmin = ctx.isAdmin;
  var chapterSettings = ctx.chapterSettings;
  var bumpDataVersion = ctx.bumpDataVersion;
  var dataVersion = ctx.dataVersion;

  // ── State ─────────────────────────────────────────────────
  var _gifts = useState([]);
  var gifts = _gifts[0];
  var setGifts = _gifts[1];

  var _loading = useState(true);
  var loading = _loading[0];
  var setLoading = _loading[1];

  var _error = useState(null);
  var error = _error[0];
  var setError = _error[1];

  // Filters
  var _searchName = useState("");
  var searchName = _searchName[0];
  var setSearchName = _searchName[1];

  var _filterType = useState("all");
  var filterType = _filterType[0];
  var setFilterType = _filterType[1];

  var _filterFund = useState("all");
  var filterFund = _filterFund[0];
  var setFilterFund = _filterFund[1];

  var _filterAck = useState("all");
  var filterAck = _filterAck[0];
  var setFilterAck = _filterAck[1];

  var fiscalStart = chapterSettings?.fiscalYearStartMonth || 1;
  var fiscal = getFiscalDates(fiscalStart);

  var _filterDateFrom = useState(fiscal.yearStart);
  var filterDateFrom = _filterDateFrom[0];
  var setFilterDateFrom = _filterDateFrom[1];

  var _filterDateTo = useState(new Date().toISOString().slice(0, 10));
  var filterDateTo = _filterDateTo[0];
  var setFilterDateTo = _filterDateTo[1];

  // Sort
  var _sortCol = useState("giftDate");
  var sortCol = _sortCol[0];
  var setSortCol = _sortCol[1];

  var _sortDir = useState("desc");
  var sortDir = _sortDir[0];
  var setSortDir = _sortDir[1];

  // Selection
  var _selected = useState(new Set());
  var selected = _selected[0];
  var setSelected = _selected[1];

  // Modals
  var _receiptGift = useState(null);
  var receiptGift = _receiptGift[0];
  var setReceiptGift = _receiptGift[1];

  var _receiptMember = useState(null);
  var receiptMember = _receiptMember[0];
  var setReceiptMember = _receiptMember[1];

  var _showNewGift = useState(false);
  var showNewGift = _showNewGift[0];
  var setShowNewGift = _showNewGift[1];

  var _editGift = useState(null);
  var editGift = _editGift[0];
  var setEditGift = _editGift[1];

  // Bulk ack
  var _bulkAcking = useState(false);
  var bulkAcking = _bulkAcking[0];
  var setBulkAcking = _bulkAcking[1];

  var _bulkError = useState(null);
  var bulkError = _bulkError[0];
  var setBulkError = _bulkError[1];

  // Batch state
  var _activeBatch = useState(null);
  var activeBatch = _activeBatch[0];
  var setActiveBatch = _activeBatch[1];

  var _batches = useState([]);
  var batches = _batches[0];
  var setBatches = _batches[1];

  var _showBatchList = useState(false);
  var showBatchList = _showBatchList[0];
  var setShowBatchList = _showBatchList[1];

  // New-batch modal state
  var _showNewBatchModal = useState(false);
  var showNewBatchModal = _showNewBatchModal[0];
  var setShowNewBatchModal = _showNewBatchModal[1];

  var _newBatchDescription = useState("");
  var newBatchDescription = _newBatchDescription[0];
  var setNewBatchDescription = _newBatchDescription[1];

  var _creatingBatch = useState(false);
  var creatingBatch = _creatingBatch[0];
  var setCreatingBatch = _creatingBatch[1];

  // Load recent batches
  var loadBatches = useCallback(async function () {
    try {
      var data = await batchService.getBatches({ limit: 20 });
      setBatches(data);
    } catch (err) {
      // silent
    }
  }, []);

  useEffect(function () {
    loadBatches();
  }, [loadBatches]);

  var handleNewBatch = function () {
    setNewBatchDescription("");
    setShowNewBatchModal(true);
  };

  var handleConfirmNewBatch = async function () {
    var description = newBatchDescription.trim();
    if (!description) {
      showMessage("Please enter a description for this batch.", "error");
      return;
    }
    setCreatingBatch(true);
    try {
      var batch = await batchService.createBatch(
        description,
        new Date().toISOString().slice(0, 10),
        CHAPTER_ID,
        currentUser?.id
      );
      setActiveBatch(batch);
      loadBatches();
      setShowNewBatchModal(false);
      setNewBatchDescription("");
    } catch (err) {
      showMessage("Failed to create batch: " + err.message, "error");
    } finally {
      setCreatingBatch(false);
    }
  };

  var handleBatchUpdate = async function () {
    // Refresh the active batch and batch list
    if (activeBatch) {
      try {
        var updated = await batchService.getBatchById(activeBatch.id);
        setActiveBatch(updated);
      } catch (err) {
        setActiveBatch(null);
      }
    }
    loadBatches();
    loadGifts();
  };

  // ── Load gifts ────────────────────────────────────────────
  var loadGifts = useCallback(
    async function () {
      setLoading(true);
      setError(null);
      showLoading("Loading gifts...");
      try {
        var filters = {
          startDate: filterDateFrom || null,
          endDate: filterDateTo || null,
          giftType: filterType !== "all" ? filterType : null,
          fundId: filterFund !== "all" ? filterFund : null,
        };
        if (filterAck !== "all") {
          filters.acknowledged = filterAck === "yes";
        }
        var rows = await giftsService.getFiltered(filters);
        setGifts(rows || []);
        setSelected(new Set());
      } catch (err) {
        setError("Could not load gifts — " + err.message);
      } finally {
        setLoading(false);
        hideLoading();
      }
    },
    [filterDateFrom, filterDateTo, filterType, filterFund, filterAck, dataVersion, showLoading, hideLoading]
  );

  useEffect(
    function () {
      loadGifts();
    },
    [loadGifts]
  );

  // ── Member lookup ─────────────────────────────────────────
  var getMember = function (memberId) {
    if (!members || !memberId) return null;
    return (
      members.find(function (m) {
        return m.id === memberId;
      }) || null
    );
  };

  var getFundName = function (fundId) {
    if (!funds || !fundId) return "—";
    var f = funds.find(function (x) {
      return x.id === fundId;
    });
    return f ? f.name : "—";
  };

  // ── Filter by member name search ──────────────────────────
  var visibleGifts = gifts.filter(function (g) {
    if (!searchName.trim()) return true;
    var m = getMember(g.memberId);
    if (!m) return false;
    var name = ((m.firstName || "") + " " + (m.lastName || "")).toLowerCase();
    return name.includes(searchName.trim().toLowerCase());
  });

  // ── Sort ──────────────────────────────────────────────────
  var sorted = visibleGifts.slice().sort(function (a, b) {
    var av, bv;
    if (sortCol === "giftDate") {
      av = a.giftDate || "";
      bv = b.giftDate || "";
    } else if (sortCol === "amount") {
      av = a.amount || 0;
      bv = b.amount || 0;
    } else if (sortCol === "member") {
      var ma = getMember(a.memberId);
      var mb = getMember(b.memberId);
      av = ma ? ((ma.lastName || "") + (ma.firstName || "")).toLowerCase() : "";
      bv = mb ? ((mb.lastName || "") + (mb.firstName || "")).toLowerCase() : "";
    } else if (sortCol === "type") {
      av = a.giftType || "";
      bv = b.giftType || "";
    } else {
      av = "";
      bv = "";
    }
    if (av < bv) return sortDir === "asc" ? -1 : 1;
    if (av > bv) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  var handleSort = function (col) {
    if (sortCol === col) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortCol(col);
      setSortDir("desc");
    }
  };

  // ── Selection ─────────────────────────────────────────────
  var toggleSelect = function (id) {
    setSelected(function (prev) {
      var next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  var toggleSelectAll = function () {
    if (selected.size === sorted.length && sorted.length > 0) {
      setSelected(new Set());
    } else {
      setSelected(
        new Set(
          sorted.map(function (g) {
            return g.id;
          })
        )
      );
    }
  };

  var unackedSelected = sorted.filter(function (g) {
    return selected.has(g.id) && !g.acknowledgedAt;
  });

  // ── Bulk acknowledge ──────────────────────────────────────
  var handleBulkAck = async function () {
    if (unackedSelected.length === 0 || bulkAcking) return;
    setBulkAcking(true);
    setBulkError(null);
    try {
      var ids = unackedSelected.map(function (g) {
        return g.id;
      });
      await giftsService.bulkMarkAcknowledged(ids);
      await loadGifts();
      setSelected(new Set());
      bumpDataVersion();
    } catch (err) {
      setBulkError("Bulk acknowledge failed. Please try again.");
    } finally {
      setBulkAcking(false);
    }
  };

  // ── Open receipt modal ────────────────────────────────────
  var handleReceipt = function (gift) {
    var m = getMember(gift.memberId);
    setReceiptGift(gift);
    setReceiptMember(m);
  };

  // ═══════════════════════════════════════════════════════════
  // STYLES — Card-safe (text1/text2/text3 on light cardBg)
  // ═══════════════════════════════════════════════════════════

  var filterBar = {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 24px",
    borderBottom: "1px solid " + palette.cardBorder,
    background: palette.toolbarBg,
    flexWrap: "wrap",
    flexShrink: 0,
    fontFamily: FONT_FAMILY,
  };

  var inputStyle = {
    fontSize: FONT_SIZES.sm,
    padding: "6px 10px",
    borderRadius: 6,
    border: "1px solid " + palette.inputBorder,
    background: palette.inputBg,
    color: palette.inputColor,
    fontFamily: FONT_FAMILY,
    outline: "none",
  };

  var selectStyle = Object.assign({}, inputStyle, { cursor: "pointer" });

  var actionBar = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 24px",
    borderBottom: "1px solid " + palette.cardBorder,
    background: palette.toolbarBg,
    flexShrink: 0,
    minHeight: 42,
    fontFamily: FONT_FAMILY,
  };

  var btnBase = {
    display: "flex",
    alignItems: "center",
    gap: 5,
    padding: "6px 12px",
    borderRadius: 6,
    fontSize: FONT_SIZES.sm,
    fontWeight: 600,
    fontFamily: FONT_FAMILY,
    cursor: "pointer",
    border: "none",
    transition: "opacity 0.15s",
  };

  var btnPrimary = Object.assign({}, btnBase, {
    background: palette.accent1,
    color: "#ffffff",
  });

  var btnSecondary = Object.assign({}, btnBase, {
    background: "transparent",
    color: palette.text2,
    border: "1px solid " + palette.inputBorder,
  });

  var btnGhost = Object.assign({}, btnBase, {
    background: "rgba(255,255,255,0.15)",
    color: palette.textOnGrad,
    border: "1px solid rgba(255,255,255,0.25)",
  });

  var btnGreen = Object.assign({}, btnBase, {
    background:
      unackedSelected.length > 0 ? "#10b981" : "#f3f4f6",
    color: unackedSelected.length > 0 ? "#ffffff" : palette.text3,
    opacity: unackedSelected.length > 0 ? 1 : 0.5,
    cursor: unackedSelected.length > 0 ? "pointer" : "default",
  });

  var tableWrap = {
    flex: 1,
    overflowY: "auto",
    padding: "0 24px 20px",
  };

  var th = {
    fontSize: FONT_SIZES.xs,
    fontWeight: 700,
    color: palette.text2,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    padding: "10px 8px",
    textAlign: "left",
    borderBottom: "2px solid " + palette.cardBorder,
    cursor: "pointer",
    userSelect: "none",
    whiteSpace: "nowrap",
    background: palette.cardBg,
    position: "sticky",
    top: 0,
    zIndex: 2,
  };

  var thNoSort = Object.assign({}, th, { cursor: "default" });

  var td = {
    fontSize: FONT_SIZES.sm,
    color: palette.text1,
    padding: "9px 8px",
    borderBottom: "1px solid " + palette.cardBorder,
    verticalAlign: "middle",
  };

  var SortIcon = function (col) {
    if (sortCol !== col) return null;
    return sortDir === "asc"
      ? React.createElement(ChevronUp, {
          size: 12,
          style: { display: "inline", marginLeft: 3 },
        })
      : React.createElement(ChevronDown, {
          size: 12,
          style: { display: "inline", marginLeft: 3 },
        });
  };

  var allSelected = sorted.length > 0 && selected.size === sorted.length;

  // ═══════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════

  return (
    <ViewLayout>
      {/* ── Zone 1: Navigation ────────────────────────────── */}
      <ZoneOneHeader
        title="Gifts"
        subtitle={
          loading
            ? "Loading…"
            : sorted.length +
              " gift" +
              (sorted.length !== 1 ? "s" : "") +
              " shown"
        }
        icon={Gift}
        onBack={function () {
          navigate("main");
        }}
        onHome={function () {
          navigate("main");
        }}
        helpContent={HELP.gifts}
      />
      <ZoneTwoToolbar />

      {/* ── Zone 2: Data Operations + Filter Bar ─────────── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "8px 16px",
          borderBottom: "1px solid " + palette.cardBorder,
          background: palette.cardBg,
          flexShrink: 0,
          flexWrap: "wrap",
          fontFamily: FONT_FAMILY,
        }}
      >
        <button style={btnPrimary} onClick={handleNewBatch}>
          <Layers size={ICON_SIZES.sm} />
          New Batch
        </button>
        {batches.length > 0 && (
          <button
            style={btnSecondary}
            onClick={function () { setShowBatchList(!showBatchList); }}
          >
            <Layers size={ICON_SIZES.sm} />
            Batches ({batches.length})
          </button>
        )}
        <button
          style={btnSecondary}
          onClick={function () {
            exportCsv(sorted, funds, members);
          }}
        >
          <Download size={ICON_SIZES.sm} />
          Export
        </button>
        <button style={btnSecondary} onClick={loadGifts} title="Refresh">
          <RefreshCw size={ICON_SIZES.sm} />
        </button>

        {/* Spacer pushes filters right */}
        <div style={{ flex: 1 }} />
      </div>

      {/* ── Zone 2: Filter Bar ────────────────────────────── */}
      <div style={filterBar}>
        <div
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Search
            size={ICON_SIZES.sm}
            style={{
              position: "absolute",
              left: 8,
              color: palette.text3,
            }}
          />
          <input
            style={Object.assign({}, inputStyle, {
              paddingLeft: 28,
              width: 180,
            })}
            placeholder="Search donor name…"
            value={searchName}
            onChange={function (e) {
              setSearchName(e.target.value);
            }}
          />
          {searchName && (
            <button
              onClick={function () {
                setSearchName("");
              }}
              style={{
                position: "absolute",
                right: 8,
                background: "none",
                border: "none",
                cursor: "pointer",
                color: palette.text3,
                padding: 0,
              }}
            >
              <X size={ICON_SIZES.xs} />
            </button>
          )}
        </div>

        <select
          style={selectStyle}
          value={filterType}
          onChange={function (e) {
            setFilterType(e.target.value);
          }}
        >
          <option value="all">All Types</option>
          <option value="cash">Cash / Check / CC</option>
          <option value="inkind">In-Kind</option>
          <option value="stock">Stock</option>
          <option value="daf">Donor Advised Fund</option>
        </select>

        <select
          style={selectStyle}
          value={filterFund}
          onChange={function (e) {
            setFilterFund(e.target.value);
          }}
        >
          <option value="all">All Funds</option>
          {(funds || []).map(function (f) {
            return (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            );
          })}
        </select>

        <select
          style={selectStyle}
          value={filterAck}
          onChange={function (e) {
            setFilterAck(e.target.value);
          }}
        >
          <option value="all">All Status</option>
          <option value="no">Pending</option>
          <option value="yes">Acknowledged</option>
        </select>

        <input
          type="date"
          style={inputStyle}
          value={filterDateFrom}
          onChange={function (e) {
            setFilterDateFrom(e.target.value);
          }}
          title="From date"
        />
        <span
          style={{
            fontSize: FONT_SIZES.xs,
            color: palette.text3,
          }}
        >
          to
        </span>
        <input
          type="date"
          style={inputStyle}
          value={filterDateTo}
          onChange={function (e) {
            setFilterDateTo(e.target.value);
          }}
          title="To date"
        />

        {(filterType !== "all" ||
          filterFund !== "all" ||
          filterAck !== "all" ||
          filterDateFrom ||
          filterDateTo) && (
          <button
            style={Object.assign({}, btnSecondary, {
              fontSize: 11,
              padding: "4px 10px",
            })}
            onClick={function () {
              setFilterType("all");
              setFilterFund("all");
              setFilterAck("all");
              setFilterDateFrom("");
              setFilterDateTo("");
            }}
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* ── Zone 2: Action Bar ────────────────────────────── */}
      <div style={actionBar}>
        {selected.size > 0 ? (
          <>
            <span
              style={{
                fontSize: FONT_SIZES.xs,
                color: palette.text1,
                fontWeight: 600,
              }}
            >
              {selected.size} selected
            </span>
            <button
              style={btnGreen}
              onClick={handleBulkAck}
              disabled={bulkAcking || unackedSelected.length === 0}
            >
              {bulkAcking
                ? React.createElement(Loader, {
                    size: 13,
                    style: { animation: "spin 1s linear infinite" },
                  })
                : React.createElement(CheckSquare, { size: 13 })}
              {bulkAcking
                ? "Acknowledging…"
                : "Mark Acknowledged (" + unackedSelected.length + ")"}
            </button>
            <button
              style={btnSecondary}
              onClick={function () {
                setSelected(new Set());
              }}
            >
              <X size={ICON_SIZES.sm} />
              Clear
            </button>
            {bulkError && (
              <span
                style={{
                  fontSize: FONT_SIZES.xs,
                  color: "#dc2626",
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <AlertCircle size={ICON_SIZES.xs} /> {bulkError}
              </span>
            )}
          </>
        ) : (
          <span
            style={{
              fontSize: FONT_SIZES.xs,
              color: palette.text3,
            }}
          >
            Select rows to bulk acknowledge
          </span>
        )}
      </div>

      {/* ── Batch List (toggle) ────────────────────────────── */}
      {showBatchList && (
        <div style={{ padding: "12px 16px", borderBottom: "1px solid " + palette.cardBorder }}>
          <div style={{ fontSize: FONT_SIZES.xs, fontWeight: 700, color: palette.text2, fontFamily: FONT_FAMILY, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.5px" }}>
            Recent Batches
          </div>
          {batches.map(function (b) {
            return (
              <div
                key={b.id}
                onClick={function () { setActiveBatch(b); setShowBatchList(false); }}
                style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "8px 12px", borderRadius: 6, cursor: "pointer",
                  border: "1px solid " + palette.cardBorder, marginBottom: 4,
                  background: palette.cardBg, fontFamily: FONT_FAMILY, fontSize: FONT_SIZES.xs,
                }}
                onMouseEnter={function (e) { e.currentTarget.style.background = palette.accent1 + "08"; }}
                onMouseLeave={function (e) { e.currentTarget.style.background = palette.cardBg; }}
              >
                <div>
                  <span style={{ fontWeight: 700, color: palette.text1 }}>{b.batch_number}</span>
                  <span style={{ color: palette.text3, marginLeft: 8 }}>{b.batch_date}</span>
                  {b.notes && <span style={{ color: palette.text3, marginLeft: 8, fontStyle: "italic" }}>{b.notes}</span>}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontWeight: 600, color: palette.accent1 }}>
                    {b.gift_count} gifts · ${Number(b.total_amount || 0).toLocaleString()}
                  </span>
                  <span style={{
                    fontSize: "0.6rem", fontWeight: 600, padding: "2px 6px", borderRadius: 8,
                    background: b.status === "open" ? "#dcfce7" : "#f3f4f6",
                    color: b.status === "open" ? "#16a34a" : "#6b7280",
                  }}>
                    {b.status}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Content: Table ────────────────────────────────── */}
      <div style={tableWrap}>
        {error && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "16px 0",
              fontSize: FONT_SIZES.sm,
              color: "#dc2626",
            }}
          >
            <AlertCircle size={ICON_SIZES.sm} /> {error}
          </div>
        )}

        {loading && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "24px 0",
              fontSize: FONT_SIZES.sm,
              color: palette.text3,
            }}
          >
            <Loader
              size={ICON_SIZES.sm}
              style={{ animation: "spin 1s linear infinite" }}
            />{" "}
            Loading gifts…
          </div>
        )}

        {!loading && !error && sorted.length === 0 && (
          <div
            style={{
              padding: "32px 0",
              textAlign: "center",
              fontSize: FONT_SIZES.sm,
              color: palette.text3,
              fontStyle: "italic",
            }}
          >
            No gifts match your filters.
          </div>
        )}

        {!loading && !error && sorted.length > 0 && (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={thNoSort}>
                  <div
                    onClick={toggleSelectAll}
                    style={{
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {allSelected
                      ? React.createElement(CheckSquare, {
                          size: 15,
                          style: { color: palette.accent1 },
                        })
                      : React.createElement(Square, {
                          size: 15,
                          style: {
                            color: palette.text3,
                          },
                        })}
                  </div>
                </th>
                <th
                  style={th}
                  onClick={function () {
                    handleSort("giftDate");
                  }}
                >
                  Date {SortIcon("giftDate")}
                </th>
                <th
                  style={th}
                  onClick={function () {
                    handleSort("member");
                  }}
                >
                  Donor {SortIcon("member")}
                </th>
                <th
                  style={th}
                  onClick={function () {
                    handleSort("type");
                  }}
                >
                  Type {SortIcon("type")}
                </th>
                <th
                  style={Object.assign({}, th, { textAlign: "right" })}
                  onClick={function () {
                    handleSort("amount");
                  }}
                >
                  Gross {SortIcon("amount")}
                </th>
                <th style={Object.assign({}, th, { textAlign: "right" })}>
                  G&S FMV
                </th>
                <th style={Object.assign({}, th, { textAlign: "right" })}>
                  Net Deductible
                </th>
                <th style={thNoSort}>Fund</th>
                <th style={thNoSort}>Status</th>
                <th style={thNoSort}>Receipt #</th>
                <th style={thNoSort}></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(function (g) {
                var m = getMember(g.memberId);
                var memberName = m
                  ? ((m.firstName || "") + " " + (m.lastName || "")).trim() ||
                    m.credentialedFullName ||
                    "Unknown"
                  : "Unknown";
                var isSelected = selected.has(g.id);

                return (
                  <tr
                    key={g.id}
                    style={{
                      background: isSelected
                        ? palette.accent1 + "08"
                        : "transparent",
                    }}
                  >
                    <td style={td}>
                      <div
                        onClick={function () {
                          toggleSelect(g.id);
                        }}
                        style={{
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {isSelected
                          ? React.createElement(CheckSquare, {
                              size: 15,
                              style: { color: palette.accent1 },
                            })
                          : React.createElement(Square, {
                              size: 15,
                              style: {
                                color: palette.textOnGrad,
                                opacity: 0.5,
                              },
                            })}
                      </div>
                    </td>

                    <td
                      style={Object.assign({}, td, {
                        whiteSpace: "nowrap",
                        opacity: 0.8,
                      })}
                    >
                      {g.giftDate
                        ? new Date(g.giftDate + "T00:00:00").toLocaleDateString(
                            "en-US",
                            {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            }
                          )
                        : "—"}
                    </td>

                    <td style={td}>
                      {m ? (
                        <span
                          onClick={function () {
                            navigate("memberDetail", m);
                          }}
                          style={{
                            fontWeight: 600,
                            color: palette.accent1,
                            cursor: "pointer",
                          }}
                          title="View donor record"
                        >
                          {memberName}
                        </span>
                      ) : (
                        <span style={{ fontWeight: 600 }}>{memberName}</span>
                      )}
                    </td>

                    <td style={td}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <span
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            background:
                              TYPE_COLORS[g.giftType] || palette.accent1,
                            flexShrink: 0,
                          }}
                        />
                        <span
                          style={{
                            fontSize: FONT_SIZES.xs,
                            opacity: 0.8,
                          }}
                        >
                          {getGiftTypeLabel(g.giftType, true)}
                        </span>
                      </div>
                    </td>

                    <td
                      style={Object.assign({}, td, {
                        textAlign: "right",
                        fontWeight: 700,
                      })}
                    >
                      {g.amount != null ? formatCurrency(g.amount) : "—"}
                    </td>

                    <td
                      style={Object.assign({}, td, {
                        textAlign: "right",
                        opacity: 0.8,
                      })}
                    >
                      {g.fairMarketValue > 0
                        ? formatCurrency(g.fairMarketValue)
                        : ""}
                    </td>

                    <td
                      style={Object.assign({}, td, {
                        textAlign: "right",
                        opacity: 0.8,
                      })}
                    >
                      {g.deductibleAmount != null
                        ? formatCurrency(g.deductibleAmount)
                        : "—"}
                    </td>

                    <td
                      style={Object.assign({}, td, {
                        fontSize: FONT_SIZES.xs,
                        opacity: 0.8,
                      })}
                    >
                      {g.fund?.name || getFundName(g.fundId)}
                    </td>

                    <td style={td}>
                      <AckBadge gift={g} palette={palette} />
                    </td>

                    <td
                      style={Object.assign({}, td, {
                        fontSize: FONT_SIZES.xs,
                        opacity: 0.6,
                        fontFamily: "monospace",
                      })}
                    >
                      {g.receiptNumber || "—"}
                    </td>

                    <td style={Object.assign({}, td, { whiteSpace: "nowrap" })}>
                      <button
                        onClick={function () {
                          handleReceipt(g);
                        }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                          padding: "4px 10px",
                          borderRadius: 5,
                          fontSize: 11,
                          fontWeight: 600,
                          background: palette.accent1 + "10",
                          border: "1px solid " + palette.accent1 + "30",
                          color: palette.accent1,
                          cursor: "pointer",
                          fontFamily: FONT_FAMILY,
                        }}
                      >
                        <Receipt size={ICON_SIZES.xs} />
                        Receipt
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Zone 3: Footer ────────────────────────────────── */}
      <ZoneThreeFooter />

      {/* ── New Batch Modal ──────────────────────────────── */}
      {showNewBatchModal && (
        <Modal
          title="New Gift Batch"
          icon={Layers}
          onClose={function () {
            if (!creatingBatch) setShowNewBatchModal(false);
          }}
          width={480}
        >
          <div style={{ fontFamily: FONT_FAMILY }}>
            <label
              style={{
                display: "block",
                fontSize: FONT_SIZES.sm,
                fontWeight: 600,
                color: palette.text2,
                marginBottom: 6,
              }}
            >
              Batch Description
            </label>
            <input
              type="text"
              autoFocus
              value={newBatchDescription}
              onChange={function (e) {
                setNewBatchDescription(e.target.value);
              }}
              onKeyDown={function (e) {
                if (e.key === "Enter" && !creatingBatch) {
                  handleConfirmNewBatch();
                }
              }}
              placeholder="e.g. April mail-in appeal, Gala envelopes"
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 8,
                border: "1px solid " + palette.inputBorder,
                background: palette.inputBg,
                color: palette.inputColor,
                fontSize: FONT_SIZES.sm,
                fontFamily: FONT_FAMILY,
                outline: "none",
                boxSizing: "border-box",
              }}
            />
            <div
              style={{
                fontSize: FONT_SIZES.xs,
                color: palette.text3,
                marginTop: 6,
              }}
            >
              A short description helps identify this batch on reports and in
              the batch list.
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
                marginTop: 20,
              }}
            >
              <ActionButton
                label="Cancel"
                variant="secondary"
                onClick={function () {
                  setShowNewBatchModal(false);
                }}
                disabled={creatingBatch}
              />
              <ActionButton
                label={creatingBatch ? "Creating..." : "Create Batch"}
                variant="primary"
                icon={Layers}
                onClick={handleConfirmNewBatch}
                disabled={creatingBatch || !newBatchDescription.trim()}
              />
            </div>
          </div>
        </Modal>
      )}

      {/* ── New / Edit Gift Modal ─────────────────────────── */}
      {showNewGift && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            zIndex: 900,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
          }}
          onClick={function (e) {
            if (e.target === e.currentTarget) {
              setShowNewGift(false);
              setEditGift(null);
            }
          }}
        >
          <div
            style={{
              background: palette.cardBg,
              borderRadius: 10,
              boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
              width: "100%",
              maxWidth: 560,
              maxHeight: "90vh",
              overflowY: "auto",
              padding: 24,
            }}
          >
            <div
              style={{
                fontSize: FONT_SIZES.md,
                fontWeight: 700,
                color: palette.text1,
                marginBottom: 16,
              }}
            >
              {editGift ? "Edit Gift" : "New Gift"}
            </div>
            <GiftEntryForm
              member={null}
              gift={editGift}
              onSaved={function () {
                setShowNewGift(false);
                setEditGift(null);
                loadGifts();
              }}
              onCancel={function () {
                setShowNewGift(false);
                setEditGift(null);
              }}
              palette={palette}
              activeFunds={activeFunds}
              showTypeSelector={true}
              showMemberSearch={true}
              members={members}
            />
          </div>
        </div>
      )}

      {/* ── Receipt Modal ──────────────────────────────────── */}
      {receiptGift && receiptMember && (
        <GiftReceiptModal
          gift={receiptGift}
          member={receiptMember}
          onClose={function () {
            setReceiptGift(null);
            setReceiptMember(null);
          }}
          onAcknowledged={function (giftId) {
            setGifts(function (prev) {
              return prev.map(function (g) {
                return g.id === giftId
                  ? Object.assign({}, g, {
                      acknowledgedAt: new Date().toISOString(),
                    })
                  : g;
              });
            });
            setReceiptGift(null);
            setReceiptMember(null);
          }}
        />
      )}

      {/* ── Batch Entry Modal ────────────────────────────────── */}
      {activeBatch && (
        <BatchEntryPanel
          batch={activeBatch}
          onBatchUpdate={handleBatchUpdate}
          onClose={function () { setActiveBatch(null); loadBatches(); loadGifts(); }}
        />
      )}
    </ViewLayout>
  );
};
